import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { Chip, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useNotificationRecipients } from '../hooks/useNotificationCampaigns';
import type {
  CampaignAudienceType,
  CampaignChannel,
  CampaignEmailRecipient,
  CampaignRecipient,
  EmailDeliveryStatus,
} from '../types';

interface RecipientsTableProps {
  campaignPublicId: string;
  audienceType: CampaignAudienceType;
  channels: CampaignChannel[];
}

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '-');
const EMAIL_STATUSES: EmailDeliveryStatus[] = [
  'QUEUED',
  'SENT',
  'DELIVERED',
  'BOUNCED',
  'COMPLAINED',
  'FAILED',
  'SUPPRESSED',
  'CANCELLED',
];
const emailStatusColor = (status: EmailDeliveryStatus) => {
  if (status === 'DELIVERED' || status === 'SENT') return 'success';
  if (status === 'FAILED' || status === 'BOUNCED' || status === 'COMPLAINED') return 'error';
  if (status === 'SUPPRESSED' || status === 'CANCELLED') return 'warning';
  return 'default';
};

export const RecipientsTable: React.FC<RecipientsTableProps> = ({
  campaignPublicId,
  audienceType,
  channels,
}) => {
  const [channel, setChannel] = useState<'in_app' | 'email'>('in_app');
  const [readFilter, setReadFilter] = useState<'read' | 'unread' | ''>('');
  const [statusFilter, setStatusFilter] = useState<EmailDeliveryStatus | ''>('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });

  useEffect(() => {
    if (!channels.includes('IN_APP') && channels.includes('EMAIL')) setChannel('email');
  }, [channels]);

  const { data, isLoading } = useNotificationRecipients(campaignPublicId, {
    page: pagination.page + 1,
    limit: pagination.pageSize,
    channel,
    read: channel === 'in_app' ? readFilter || undefined : undefined,
    status: channel === 'email' ? statusFilter || undefined : undefined,
  });

  const inAppColumns = useMemo<GridColDef<CampaignRecipient>[]>(
    () => [
      {
        field: 'user',
        headerName: 'User',
        flex: 1,
        minWidth: 240,
        renderCell: (params) => (
          <div className="min-w-0">
            <div className="truncate font-semibold text-gray-900 dark:text-white">
              {params.row.user.email}
            </div>
            <div className="truncate text-xs text-gray-500 dark:text-slate-400">
              {params.row.user.userName || params.row.user.publicId}
            </div>
          </div>
        ),
      },
      {
        field: 'readAt',
        headerName: 'Read',
        width: 130,
        renderCell: (params) => (
          <Chip
            label={params.row.readAt ? 'Read' : 'Unread'}
            size="small"
            color={params.row.readAt ? 'success' : 'default'}
          />
        ),
      },
      {
        field: 'deliveredAt',
        headerName: 'Delivered',
        width: 180,
        valueFormatter: (value) => formatDate(value as string),
      },
      {
        field: 'archivedAt',
        headerName: 'Archived',
        width: 180,
        valueFormatter: (value) => formatDate(value as string | null),
      },
    ],
    []
  );
  const emailColumns = useMemo<GridColDef<CampaignEmailRecipient>[]>(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 240,
      },
      {
        field: 'user',
        headerName: 'User',
        width: 220,
        renderCell: (params) =>
          params.row.user ? (
            <div className="min-w-0">
              <div className="truncate font-semibold text-gray-900 dark:text-white">
                {params.row.user.email}
              </div>
              <div className="truncate text-xs text-gray-500 dark:text-slate-400">
                {params.row.user.userName || params.row.user.publicId}
              </div>
            </div>
          ) : (
            '-'
          ),
      },
      { field: 'locale', headerName: 'Locale', width: 90 },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        renderCell: (params) => (
          <Chip
            label={params.row.status}
            size="small"
            color={emailStatusColor(params.row.status)}
          />
        ),
      },
      {
        field: 'error',
        headerName: 'Error',
        width: 240,
        renderCell: (params) =>
          params.row.error ? (
            <Tooltip title={params.row.error}>
              <span className="truncate text-sm text-red-600 dark:text-red-300">
                {params.row.error}
              </span>
            </Tooltip>
          ) : (
            '-'
          ),
      },
      {
        field: 'sentAt',
        headerName: 'Sent',
        width: 180,
        valueFormatter: (value) => formatDate(value as string | null),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 180,
        valueFormatter: (value) => formatDate(value as string),
      },
    ],
    []
  );
  const rows = data?.items || [];

  return (
    <div className="space-y-3">
      {audienceType === 'ALL' && (
        <p className="text-sm text-gray-500 dark:text-slate-400">
          For ALL campaigns, only users who opened the app are listed.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={channel}
          onChange={(_, next) => {
            if (!next) return;
            setChannel(next);
            setPagination((current) => ({ ...current, page: 0 }));
          }}
        >
          {channels.includes('IN_APP') && <ToggleButton value="in_app">In-app</ToggleButton>}
          {channels.includes('EMAIL') && <ToggleButton value="email">Email</ToggleButton>}
        </ToggleButtonGroup>
        {channel === 'in_app' ? (
          <select
            value={readFilter}
            onChange={(event) => {
              setReadFilter(event.target.value as 'read' | 'unread' | '');
              setPagination((current) => ({ ...current, page: 0 }));
            }}
            className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          >
            <option value="">All recipients</option>
            <option value="read">Read</option>
            <option value="unread">Unread</option>
          </select>
        ) : (
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as EmailDeliveryStatus | '');
              setPagination((current) => ({ ...current, page: 0 }));
            }}
            className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          >
            <option value="">All statuses</option>
            {EMAIL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="rounded-lg bg-white shadow-sm dark:bg-slate-800" style={{ height: 520 }}>
        <DataGrid
          rows={rows}
          getRowId={(row) =>
            channel === 'email'
              ? `${(row as CampaignEmailRecipient).email}-${(row as CampaignEmailRecipient).updatedAt}`
              : (row as CampaignRecipient).user.publicId
          }
          columns={channel === 'email' ? emailColumns : inAppColumns}
          rowCount={data?.total || 0}
          loading={isLoading}
          paginationMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          sx={{ border: 'none' }}
        />
      </div>
    </div>
  );
};
